/**
 * Display mode: "3d" or "lite" (MASTER_PROMPT §6.5).
 *
 * 3D   the full look: frosted-glass blur, the drifting ambient backdrop, and
 *      WebGL scenes (the welcome globe now; the 3D world in Phase 3).
 * Lite 2D only: no backdrop blur, no ambient animation, CSS stand-ins instead
 *      of WebGL. Every number and action is the same; only the decoration goes.
 *
 * The player picks Auto (default), Lite or 3D in Save / Load → Display. Auto
 * picks Lite on a device that asks for less motion, has no WebGL, has 2 or
 * fewer CPU cores or 2 GB or less memory, has Data Saver on, or is a
 * touch device with 4 or fewer cores or 3 GB or less memory (a low-end
 * phone). It also learns: if the 3D world measures under 30 fps for a few
 * seconds even at the lowest resolution (world/scene.js -> reportLowFps),
 * Auto switches this device to Lite and remembers why.
 *
 * The mode lives on <html data-display="lite|3d">, which CSS keys off, and
 * changes fire a "ges:display" event on window. three.js (vendored in
 * frontend/vendor/three) is fetched on first use, only in 3D mode: loadThree().
 */

const DISPLAY_KEY = "ges.display"; // "auto" | "lite" | "3d"
const MEASURED_KEY = "ges.display.measured"; // why Auto moved this device to Lite, from a real fps reading

function displayPreference() {
  try {
    const v = localStorage.getItem(DISPLAY_KEY);
    return v === "lite" || v === "3d" ? v : "auto";
  } catch (e) { return "auto"; }
}

let webglCache = null;
function hasWebGL() {
  if (webglCache !== null) return webglCache;
  try {
    const c = document.createElement("canvas");
    webglCache = !!(window.WebGLRenderingContext && (c.getContext("webgl") || c.getContext("experimental-webgl")));
  } catch (e) { webglCache = false; }
  return webglCache;
}

/** Why Auto would choose Lite on this device (empty = it chooses 3D). */
function liteReasons() {
  const reasons = [];
  const nav = window.navigator || {};
  if (window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) reasons.push("your system asks for reduced motion");
  if (!hasWebGL()) reasons.push("this browser has no WebGL");
  if (nav.hardwareConcurrency && nav.hardwareConcurrency <= 2) reasons.push(`${nav.hardwareConcurrency} CPU cores`);
  if (nav.deviceMemory && nav.deviceMemory <= 2) reasons.push(`${nav.deviceMemory} GB memory`);
  if (nav.connection && nav.connection.saveData) reasons.push("Data Saver is on");
  const touch = !!(window.matchMedia && window.matchMedia("(pointer: coarse)").matches);
  if (touch && ((nav.hardwareConcurrency && nav.hardwareConcurrency <= 4) || (nav.deviceMemory && nav.deviceMemory <= 3))) {
    reasons.push("a phone or tablet with modest hardware");
  }
  try { const m = localStorage.getItem(MEASURED_KEY); if (m) reasons.push(m); } catch (e) { /* none */ }
  return reasons;
}

/** The mode in effect: an explicit choice, or Auto's pick. */
function displayMode() {
  const pref = displayPreference();
  if (pref !== "auto") return pref;
  return liteReasons().length ? "lite" : "3d";
}

const is3D = () => displayMode() === "3d";

function applyDisplayMode() {
  const mode = displayMode();
  document.documentElement.dataset.display = mode;
  return mode;
}

function setDisplayPreference(pref) {
  try {
    if (pref === "auto") {
      localStorage.removeItem(DISPLAY_KEY);
      if (setDisplayPreference.fromUser) localStorage.removeItem(MEASURED_KEY); // picking Auto again re-tests this device
    } else localStorage.setItem(DISPLAY_KEY, pref);
  } catch (e) { /* private mode: applies for this page only */ }
  const mode = applyDisplayMode();
  window.dispatchEvent(new CustomEvent("ges:display", { detail: { mode, preference: pref } }));
  return mode;
}

/**
 * The 3D scene measured a low frame rate on this device. In Auto mode, switch
 * to Lite and remember the reason (shown in Save / Load → Display). Returns
 * true when it switched. An explicit 3D choice is always respected.
 */
function reportLowFps(fps) {
  if (displayPreference() !== "auto" || displayMode() === "lite") return false;
  try { localStorage.setItem(MEASURED_KEY, `the 3D world ran at ${Math.round(fps)} fps here`); } catch (e) { /* this page only */ }
  setDisplayPreference("auto");
  return displayMode() === "lite";
}

let threePromise = null;
/** The three.js module (vendor/three, r186), loaded on first use. Resolves
 *  null in Lite mode or if it fails to load. "three" resolves through the
 *  import map in index.html (dev) or the Vite alias (build). */
function loadThree() {
  if (!is3D()) return Promise.resolve(null);
  if (!threePromise) {
    threePromise = import("three").catch((e) => {
      console.warn("three.js failed to load:", e);
      threePromise = null;
      return null;
    });
  }
  return threePromise;
}

/** The Display setting as a small segmented control (used in Save / Load). */
function displaySettingHtml() {
  const pref = displayPreference();
  const reasons = liteReasons();
  const auto = reasons.length ? `Auto picked Lite: ${reasons.join(", ")}.` : "Auto picked 3D: this device can run it.";
  const opt = (v, label) => `<button type="button" class="seg-btn${pref === v ? " on" : ""}" data-display-pref="${v}" aria-pressed="${pref === v}">${label}</button>`;
  return `
    <div class="display-setting">
      <div><b>Display</b><span>3D shows the World tab and the login screen as 3D globes. Lite draws them flat, lighter on battery and older devices. Every chart is 2D either way, and the numbers are the same.</span>
        <span class="display-auto-note">${auto}</span></div>
      <div class="seg" role="group" aria-label="Display mode">${opt("auto", "Auto")}${opt("lite", "Lite")}${opt("3d", "3D")}</div>
    </div>`;
}

function wireDisplaySetting(root) {
  root.querySelectorAll("[data-display-pref]").forEach((b) => b.addEventListener("click", () => {
    setDisplayPreference.fromUser = true;
    setDisplayPreference(b.dataset.displayPref);
    setDisplayPreference.fromUser = false;
    root.querySelectorAll("[data-display-pref]").forEach((x) => {
      const on = x === b;
      x.classList.toggle("on", on);
      x.setAttribute("aria-pressed", String(on));
    });
  }));
}

applyDisplayMode();

export { applyDisplayMode, displayMode, displayPreference, displaySettingHtml, is3D, liteReasons, loadThree, reportLowFps, setDisplayPreference, wireDisplaySetting };
